import { Avatar, IconTile } from "@design";
import { Bell, BookOpen, Bot, ListChecks, Share2, Workflow } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import type { NotificationResponse } from "@/api";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { initialsOf } from "@/lib/initials";
import { describeNotification, iconFor, isFailure, type NotificationIcon } from "./notificationDisplay";

const ICONS: Record<NotificationIcon, typeof Bell> = {
  workflow: Workflow,
  task: ListChecks,
  knowledge: BookOpen,
  share: Share2,
  agent: Bot,
  bell: Bell,
};

const STRONG = <strong className="[font-weight:var(--weight-semibold)] text-content-primary" />;

function Leading({ n }: Readonly<{ n: NotificationResponse }>) {
  if (n.actor?.kind === "user") {
    return <Avatar size="md" initials={initialsOf(n.actor.name)} src={n.actor.avatarUrl ?? undefined} />;
  }
  const Icon = ICONS[iconFor(n)];
  return (
    <IconTile variant={isFailure(n.type) ? "danger" : "neutral"} size="lg">
      <Icon size={18} aria-hidden="true" />
    </IconTile>
  );
}

function Sentence({ n }: Readonly<{ n: NotificationResponse }>) {
  const { t } = useTranslation("notifications");
  const sentence = describeNotification(n);
  if (!sentence) {
    return (
      <>
        <Text as="span" variant="body-sm" weight="semibold" className="block">
          {n.title}
        </Text>
        <Text as="span" variant="body-sm" tone="secondary" className="block">
          {n.message}
        </Text>
      </>
    );
  }
  return (
    <Text as="span" variant="body-sm" tone="secondary" className="block">
      <Trans
        t={t}
        i18nKey={`sentence.${sentence.key}`}
        values={{ actor: sentence.actor, subject: sentence.subject, count: sentence.count }}
        components={{ actor: STRONG, subject: STRONG }}
      />
    </Text>
  );
}

export function NotificationItem({
  notification: n,
  onSelect,
}: Readonly<{ notification: NotificationResponse; onSelect: (n: NotificationResponse) => void }>) {
  const { t } = useTranslation("notifications");
  const unread = !n.readAt;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(n)}
        className="flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-interactive-hover btn-focus-ring"
      >
        <Leading n={n} />
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <Sentence n={n} />
          {n.context && (
            <Text as="span" variant="caption" tone="tertiary" className="block truncate">
              {n.context.join(" › ")}
            </Text>
          )}
          {n.excerpt && (
            <Text as="span" variant="body-sm" tone="secondary" className="line-clamp-2">
              {t("excerpt", { text: n.excerpt })}
            </Text>
          )}
          <Text as="span" variant="caption" tone="tertiary">
            <time dateTime={n.createdAt}>{formatRelativeTime(n.createdAt)}</time>
          </Text>
        </span>
        {unread && (
          <span className="mt-2 size-2 shrink-0 rounded-full bg-content-primary">
            <span className="sr-only">{t("unread")}</span>
          </span>
        )}
      </button>
    </li>
  );
}
